// Unified Customer Profile — personal info, wallet, transactions, card redemption, password change, sound prefs.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/api/client";
import useWebSocket from "@/hooks/useWebSocket";
import { useAuth } from "@/contexts/AuthContext";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import ProfileHeader from "@/components/profile/ProfileHeader";
import WalletCard from "@/components/profile/WalletCard";
import RedeemCardForm from "@/components/profile/RedeemCardForm";
import TransactionsList from "@/components/profile/TransactionsList";
import EditInfoModal from "@/components/profile/EditInfoModal";
import ChangePasswordModal from "@/components/profile/ChangePasswordModal";
import SoundPreferenceSection from "@/components/profile/SoundPreferenceSection";
import NotificationPreferences from "@/components/profile/NotificationPreferences";
import LoyaltyCard from "@/components/profile/LoyaltyCard";
import MyPayTopupButton from "@/components/profile/MyPayTopupButton";
import ReferralCard from "@/components/profile/ReferralCard";
import QuickLoginSettings from "@/components/QuickLoginSettings";
import { LogOut, KeyRound, Pencil, Bell } from "lucide-react";

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState({ balance: 0, transactions: [], currency: "LYD" });
  const [walletLoading, setWalletLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const loadWallet = async () => {
    try {
      const { data } = await api.get("/wallet/me");
      setWallet(data);
    } catch { /* ignore */ }
    finally { setWalletLoading(false); }
  };

  useEffect(() => { loadWallet(); }, []);

  useWebSocket({
    onMessage: (msg) => {
      if (msg.topic && msg.topic.startsWith("user:") && msg.event === "wallet.updated") {
        setWallet((prev) => ({
          ...prev,
          balance: msg.data.balance,
          transactions: [msg.data.transaction, ...(prev.transactions || [])].slice(0, 50),
        }));
      }
    },
  });

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const joinedText = useMemo(() => {
    if (!user?.created_at) return "";
    try {
      return new Date(user.created_at).toLocaleDateString("ar", { dateStyle: "long" });
    } catch { return ""; }
  }, [user?.created_at]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[var(--surface-alt)]" data-testid="profile-page">
      <TopBar/>
      <div className="max-w-3xl mx-auto px-4 pt-4 pb-2 flex items-center gap-2">
        <BackButton to="/" variant="pill"/>
        <div className="flex-1">
          <h1 className="text-2xl font-black" style={{fontFamily:"Cairo"}}>الملف الشخصي</h1>
          <p className="text-xs text-[var(--text-secondary)]">كل بياناتك ومحفظتك في مكان واحد</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pb-24 space-y-4">
        {/* Personal info */}
        <ProfileHeader user={user} joinedText={joinedText} onEdit={() => setShowEdit(true)}/>

        {/* Wallet balance */}
        <WalletCard balance={wallet.balance} loading={walletLoading} onRefresh={loadWallet}/>

        {/* Redeem card */}
        <RedeemCardForm onSuccess={(newBalance) => {
          setWallet((prev) => ({ ...prev, balance: newBalance }));
          loadWallet();
        }}/>

        {/* MyPay wallet top-up — pay via Yasr / Masrafi / Moamalat / Edfali / Sahara */}
        <MyPayTopupButton onSuccess={() => loadWallet()}/>

        {/* Loyalty points */}
        <LoyaltyCard onRedeemed={() => loadWallet()}/>

        {/* Referral program */}
        <ReferralCard/>

        {/* Notification preferences */}
        <NotificationPreferences user={user} onUpdated={refresh}/>

        {/* Sound preferences */}
        <SoundPreferenceSection user={user} onUpdated={refresh}/>

        {/* Quick unlock — PIN / biometric management */}
        <QuickLoginSettings/>

        {/* Account actions */}
        <div className="bg-white rounded-3xl talabya-shadow-soft p-5 space-y-3" data-testid="account-actions">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-primary-brand"/>
            <h2 className="font-black text-lg" style={{fontFamily:"Cairo"}}>إعدادات الحساب</h2>
          </div>
          <button
            onClick={() => setShowEdit(true)}
            className="w-full flex items-center justify-between p-3 rounded-xl bg-[var(--surface-alt)] hover:bg-blue-50 border border-transparent hover:border-primary-brand/20 transition-colors"
            data-testid="btn-edit-info">
            <div className="flex items-center gap-2 text-[var(--text-primary)]">
              <Pencil size={16} className="text-primary-brand"/>
              <span className="font-bold text-sm">تعديل المعلومات الشخصية</span>
            </div>
            <span className="text-[var(--text-secondary)] text-xs">›</span>
          </button>
          <button
            onClick={() => setShowPwd(true)}
            className="w-full flex items-center justify-between p-3 rounded-xl bg-[var(--surface-alt)] hover:bg-blue-50 border border-transparent hover:border-primary-brand/20 transition-colors"
            data-testid="btn-change-password">
            <div className="flex items-center gap-2 text-[var(--text-primary)]">
              <KeyRound size={16} className="text-primary-brand"/>
              <span className="font-bold text-sm">تغيير كلمة المرور</span>
            </div>
            <span className="text-[var(--text-secondary)] text-xs">›</span>
          </button>
          <Link
            to="/orders"
            className="w-full flex items-center justify-between p-3 rounded-xl bg-[var(--surface-alt)] hover:bg-blue-50 border border-transparent hover:border-primary-brand/20 transition-colors"
            data-testid="btn-my-orders">
            <div className="flex items-center gap-2 text-[var(--text-primary)]">
              <Bell size={16} className="text-primary-brand"/>
              <span className="font-bold text-sm">طلباتي السابقة</span>
            </div>
            <span className="text-[var(--text-secondary)] text-xs">›</span>
          </Link>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors font-black text-sm"
            data-testid="btn-logout">
            <LogOut size={16}/> تسجيل الخروج
          </button>
        </div>

        {/* Transactions */}
        <TransactionsList transactions={wallet.transactions}/>
      </div>

      {showEdit && (
        <EditInfoModal
          user={user}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); refresh(); }}
        />
      )}
      {showPwd && (
        <ChangePasswordModal
          onClose={() => setShowPwd(false)}
          onDone={() => setShowPwd(false)}
        />
      )}
    </div>
  );
}
